'use client';

import { useInView } from 'motion/react';
import { useTranslations } from 'next-intl';
import { useCallback, useRef, useState } from 'react';

import { customRichTextTags } from '@/utils/i18n';

import { SectionContainer } from '../_components';
import { CENTER } from './products';
import { DesktopFan } from './desktop-fan';
import { MobileCarousel } from './mobile-carousel';
import { NavArrow } from './nav-arrow';

import solutionBackground from '@images/sf2-background.webp';
import { useMediaQuery } from '@/hooks/useMediaQuery.hook';

export default function SolutionFinder() {
  const t = useTranslations('Products.solutionFinder');
  // active = 무한 정수(기본 GENOME). productAt(active) 로 실제 제품 매핑
  const [active, setActive] = useState(CENTER);
  const isMobile = useMediaQuery('(max-width: 767px)');

  const goTo = useCallback((index: number) => setActive(index), []);
  const prev = useCallback(() => goTo(active - 1), [active, goTo]);
  const next = useCallback(() => goTo(active + 1), [active, goTo]);

  // 스크롤 진입 시 카드 등장(양옆)을 1회 트리거
  const sectionRef = useRef<HTMLDivElement>(null);
  const entered = useInView(sectionRef, { once: true, amount: 0.5 });

  return (
    <div
      id="solution"
      className="flex scroll-mt-20 flex-col gap-y-[60px] md:gap-y-[160px]"
      style={{ scrollMarginTop: isMobile ? '60px' : '-20px' }}
    >
      <div
        ref={sectionRef}
        className="relative w-full overflow-hidden py-[60px] md:pb-0 md:pt-[160px]"
      >
        {/* 섹션 배경 이미지 (돔형 글로우) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${solutionBackground.src})` }}
        />
        <SectionContainer>
          <div className="flex flex-col items-center gap-5 md:gap-10">
            <div className="flex flex-col items-center gap-[10px] text-center md:gap-5">
              <h2 className="text-2xl font-semibold leading-8 tracking-[-0.464px] text-[#2B2E33] md:text-[48px] md:leading-[56px] md:tracking-[-1.072px]">
                {t.rich('title', customRichTextTags)}
              </h2>
              <p className="text-base font-medium leading-[22px] tracking-[-0.128px] text-[#6C7380] md:text-xl md:font-semibold md:leading-7 md:tracking-[-0.336px]">
                {t('subtitle')}
              </p>
            </div>

            {/* 데스크탑: 부채꼴 캐러셀 (화살표는 중앙 카드 하단+72px 위치에 내장) */}
            <div className="hidden w-full md:block">
              <DesktopFan
                active={active}
                goTo={goTo}
                prev={prev}
                next={next}
                entered={entered}
              />
            </div>

            {/* 모바일: 단일 카드 + 좌우 peek + 화살표 */}
            <div className="flex w-full flex-col items-center gap-12 md:hidden">
              <MobileCarousel active={active} goTo={goTo} entered={entered} />
              <div className="flex items-center gap-3">
                <NavArrow
                  direction="prev"
                  onClick={prev}
                  label={t('carousel.prev')}
                />
                <NavArrow
                  direction="next"
                  onClick={next}
                  label={t('carousel.next')}
                />
              </div>
            </div>
          </div>
        </SectionContainer>
      </div>
    </div>
  );
}
